export type Language = "cs" | "en";

export interface NavItem {
  label: string;
  href: string;
}

export interface Service {
  id: string;
  title: string;
  description: string;
  cta: string;
}

export interface TypingWord {
  text: string;
  color: string;
}

export interface ProcessStep {
  number: number;
  title: string;
  desc: string;
}

export interface WebDevWhyCard {
  title: string;
  description: string;
}

export interface WebDevProcessStep {
  title: string;
  description: string;
}

export interface WebDevFAQ {
  question: string;
  answer: string;
}

export interface WebDevPage {
  breadcrumb: {
    home: string;
    current: string;
  };
  hero: {
    typingPrefix: string;
    typingWord: TypingWord;
    typingSuffix: string;
    subheadline: string;
    cta: string;
  };
  why: {
    title: string;
    cards: WebDevWhyCard[];
  };
  process: {
    title: string;
    subtitle: string;
    cta: string;
    steps: WebDevProcessStep[];
  };
  faq: {
    title: string;
    items: WebDevFAQ[];
  };
  contact: {
    title: string;
    subtitle: string;
  };
}

export interface AIAutoUseCaseCard {
  title: string;
  description: string;
  iconColor: "green" | "cyan";
}

export interface AIAutoPage {
  breadcrumb: {
    home: string;
    current: string;
  };
  hero: {
    typingPrefix: string;
    typingWord: TypingWord;
    typingSuffix?: string;
    statement: string;
    statementSub: string;
    subheadline: string;
    cta: string;
  };
  useCases: {
    label: string;
    title: string;
    subtitle: string;
    cards: AIAutoUseCaseCard[];
  };
  whyUs: {
    title: string;
    cards: { title: string; description: string }[];
  };
  process: {
    title: string;
    subtitle: string;
    cta: string;
    steps: WebDevProcessStep[];
  };
  faq: {
    label: string;
    title: string;
    items: WebDevFAQ[];
  };
  contact: {
    title: string;
    subtitle: string;
  };
}

export interface AITrainingPage {
  breadcrumb: {
    home: string;
    current: string;
  };
  hero: {
    typingPrefix: string;
    typingWord: TypingWord;
    statement: string;
    subheadline: string;
    cta: string;
  };
  offer: {
    title: string;
    subtitle: string;
    cards: { title: string; description: string }[];
  };
  audience: {
    title: string;
    cards: { title: string; description: string }[];
  };
  process: {
    title: string;
    subtitle: string;
    cta: string;
    steps: WebDevProcessStep[];
  };
  faq: {
    title: string;
    items: WebDevFAQ[];
  };
  contact: {
    subtitle: string;
  };
}

export interface AppDevPage {
  breadcrumb: {
    home: string;
    current: string;
  };
  hero: {
    typingPrefix: string;
    typingWord: TypingWord;
    statement: string;
    subheadline: string;
    cta: string;
  };
  offer: {
    title: string;
    subtitle: string;
    cards: { title: string; description: string }[];
  };
  whyUs: {
    title: string;
    cards: { title: string; description: string }[];
  };
  process: {
    title: string;
    subtitle: string;
    cta: string;
    steps: WebDevProcessStep[];
  };
  faq: {
    title: string;
    items: WebDevFAQ[];
  };
  contact: {
    subtitle: string;
  };
}

export interface ContentData {
  nav: {
    logo: string;
    items: NavItem[];
    languageSwitch: string;
  };
  hero: {
    typingPrefix: string;
    typingWords: TypingWord[];
    resultLine: string;
    subheadline: string;
    cta: {
      primary: string;
      secondary: string;
    };
    trustIndicators: string[];
    scrollIndicator: string;
    headline: string[];
  };
  mobileMenu: {
    open: string;
    close: string;
  };
  services: {
    title: string;
    subtitle: string;
    items: Service[];
  };
  about: {
    title: string;
    blockLabels: string[];
    description: string[];
  };
  process: {
    title: string;
    subtitle: string;
    cta: string;
    steps: ProcessStep[];
  };
  contact: {
    title: string;
    subtitle: string;
    form: {
      name: string;
      email: string;
      phone: string;
      message: string;
      submit: string;
      submitting: string;
      gdpr: string;
      gdprLink: string;
    };
    success: string;
    error: string;
    details: {
      name: string;
      email: string;
      phone: string;
      ico: string;
    };
  };
  footer: {
    copyright: string;
    tagline: string;
    address: string;
    registry: string;
    cookieSettings: string;
    privacyPolicy: string;
  };
  webDev: WebDevPage;
  aiAuto: AIAutoPage;
  appDev: AppDevPage;
  aiTraining: AITrainingPage;
}
export const content: Record<Language, ContentData> = {
  cs: {
    nav: {
      logo: "AiLum Solutions",
      items: [
        { label: "Služby", href: "#sluzby" },
        { label: "Spolupráce", href: "/#spoluprace" },
        { label: "O nás", href: "/#o-nas" },
        { label: "Kontakt", href: "/#kontakt" },
      ],
      languageSwitch: "EN",
    },
    hero: {
      typingPrefix: "Děláme ",
      typingWords: [
        { text: "Moderní weby", color: "#00d4ff" },
        { text: "Aplikace", color: "#ff6b35" },
        { text: "Automatizace", color: "#7c86ff" },
        { text: "AI agenty", color: "#10b981" },
        { text: "AI Workshopy", color: "#F97316" },
      ],
      resultLine: "Digitální řešení, která posouvají váš business",
      headline: ["Moderní weby a aplikace.", "AI Automatizace.", "Digitální řešení, která posouvají váš business"],
      subheadline:
        "Rozumíme businessu i technologiím. Tvoříme webové stránky, aplikace a AI automatizace pro firmy, které chtějí růst. Pomůžeme vám získat více zákazníků, zefektivnit procesy a snížit náklady.",
      cta: {
        primary: "Chci nezávaznou konzultaci",
        secondary: "Kontaktujte nás",
      },
      trustIndicators: [
        "Rychlé a responzivní webové stránky",
        "Moderní webové a mobilní aplikace",
        "SEO optimalizace",
        "AI konzultace, automatizace a školení",
      ],
      scrollIndicator: "Proč si vybrat nás",
    },
    mobileMenu: {
      open: "Otevřít menu",
      close: "Zavřít menu",
    },
    services: {
      title: "Služby",
      subtitle: "Popište nám svůj projekt, nápad nebo problém a my se postaráme o zbytek",
      items: [
        {
          id: "weby",
          title: "Tvorba webových stránek",
          description:
            "Vytvoříme vám moderní a rychlé webové stránky na míru, které zaujmou na první pohled a přivádějí zákazníky. Responzivní design, SEO optimalizace a snadná správa obsahu — vše, co váš web potřebuje, aby pracoval pro váš business.",
          cta: "Zjistit více",
        },
        {
          id: "ai-automatizace",
          title: "Automatizace a AI řešení",
          description:
            "Propojíme vaše systémy, zautomatizujeme opakující se procesy a vytvoříme AI chatboty, voiceboty nebo AI agenty na míru. Ušetřete desítky hodin měsíčně a snižte provozní náklady — nechte AI pracovat za vás.",
          cta: "Zjistit více",
        },
        {
          id: "aplikace",
          title: "Vývoj aplikací",
          description:
            "Vyvíjíme webové a mobilní aplikace na míru — od MVP pro startupy po komplexní firemní řešení. Získáte škálovatelný software, který řeší konkrétní problém vašeho businessu, šetří čas a podporuje růst firmy.",
          cta: "Zjistit více",
        },
        {
          id: "ai-skoleni",
          title: "AI školení a workshopy",
          description:
            "Uděláme workshop ve vaší firmě a proškolíme váš tým v efektivním využívání AI nástrojů přímo na příkladech z vaší praxe. Naučíme vás pracovat s ChatGPT, Claude, Gemini, ale i dalšími AI nástroji přímo na míru vašim potřebám, aby vám reálně šetřily hodiny práce denně a zvyšovaly produktivitu celé firmy.",
          cta: "Zjistit více",
        },
      ],
    },
    about: {
      title: "O nás",
      blockLabels: ["Vývoj webů a aplikací", "AI a automatizace na míru", "Proč si vybrat AiLum Solutions"],
      description: [
        "Webové stránky a aplikace děláme s citem pro design a důrazem na detail. Spojujeme technické znalosti s UX/UI expertízou — výsledkem jsou weby a aplikace, které jsou rychlé, přehledné a příjemné na používání. Chceme, aby váš web přiváděl zákazníky a budoval důvěru, ne jen vypadal dobře.",
        "AI není jen buzzword — je to nástroj, který vám může reálně ušetřit čas a peníze. Máme zkušenosti s implementací automatizací, AI agentů i školením týmů v praxi, takže víme co funguje a co ne. Pomůžeme vám najít místa kde to dává největší smysl a nastavíme vše na míru vašemu businessu.",
        "Nejste pro nás jen další klient, váš business bereme vážně a chceme být vaším partnerem. Máme zkušenosti z businessu, marketingu, technologií i práva, takže rozumíme vašim výzvám z více úhlů a dokážeme navrhnout řešení, která skutečně fungují.\n\nMáme smysl pro detail. Nejde nám jen o pěkný web nebo aplikaci — chceme vytvořit nástroj, který vám přivede zákazníky, ušetří čas a posílí konkurenceschopnost. A nezmizíme po spuštění — zůstáváme s vámi i dlouhodobě a pomůžeme kdykoliv to bude třeba.\n\nPracujeme rychle, kvalitně a za férovou cenu. Ozvěte se a uvidíte sami, nezávazná konzultace je zdarma.",
      ],
    },
    process: {
      title: "Jak probíhá spolupráce",
      subtitle:
        "Sejdeme se, pochopíme váš business a navrhneme optimální řešení — pak už se postaráme o\u00A0zbytek. Průběžně vás budeme informovat o\u00A0postupu, takže budete mít vždy přehled o\u00A0tom, co se děje. Výsledek dostanete včas a\u00A0přesně podle vašich představ.",
      cta: "Sjednat konzultaci",
      steps: [
        {
          number: 1,
          title: "Porozumíme vašemu businessu a\u00A0vašim požadavkům",
          desc: "Na schůzce se pobavíme o vašem businessu a zjistíme, s čím vám můžeme pomoct.",
        },
        {
          number: 2,
          title: "Uděláme si analýzu",
          desc: "Analyzujeme vaše požadavky a váš problém, vymyslíme to pravé řešení.",
        },
        {
          number: 3,
          title: "Uděláme návrh designu",
          desc: "Vytvoříme design produktu, ukážeme vám ho a necháme si ho od vás schválit.",
        },
        {
          number: 4,
          title: "Naprogramujeme",
          desc: "Designu dáme život a naprogramujeme finální produkt — web, aplikaci, automatizaci nebo AI agenta.",
        },
        { number: 5, title: "Testujeme", desc: "Produkt ze všech stran otestujeme, že funguje jak má." },
        {
          number: 6,
          title: "Ostré nasazení produktu",
          desc: "Nasadíme produkt na produkci, abyste ho mohli začít používat.",
        },
        {
          number: 7,
          title: "Staráme se dál",
          desc: "Tím to pro nás nekončí. Pokud byste i potom potřebovali pomoct, jsme tu pro vás.",
        },
      ],
    },
    contact: {
      title: "Kontakt",
      subtitle: "Popište nám svůj projekt a my se ozveme. Nezávazná konzultace je zdarma.",
      form: {
        name: "Jméno",
        email: "E-mail",
        phone: "Telefon",
        message: "Zpráva",
        submit: "Odeslat zprávu",
        submitting: "...",
        gdpr: "Odesláním formuláře souhlasíte se zpracováním uvedených osobních údajů za účelem vyřízení vaší poptávky. Více informací zde:",
        gdprLink: "Zásady ochrany osobních údajů",
      },
      success: "Děkujeme za zprávu! Ozveme se vám co nejdříve.",
      error: "Zprávu se nepodařilo odeslat. Zkuste to prosím znovu.",
      details: {
        name: "Lukáš Muladi",
        email: "info@ailum.cz",
        phone: "+420 773 485 406",
        ico: "IČO: 09302794",
      },
    },
    footer: {
      copyright: "© 2025 AiLum Solutions. Všechna práva vyhrazena.",
      tagline: "Tvorba webů a aplikací. AI automatizace a školení. Digitální řešení pro firmy.",
      address: "Sídlo: Paskovská 229, Krmelín, 739 24",
      registry: "Zapsán v živnostenském rejstříku Magistrátu města Frýdek-Místek",
      cookieSettings: "Nastavení cookies",
      privacyPolicy: "Zásady ochrany osobních údajů",
    },
    webDev: {
      breadcrumb: {
        home: "Domů",
        current: "Tvorba webových stránek",
      },
      hero: {
        typingPrefix: "Děláme ",
        typingWord: { text: "moderní", color: "#00d4ff" },
        typingSuffix: " webové stránky.",
        subheadline:
          "Vytvoříme vám moderní webové stránky, které zaujmou na první pohled a přivádějí nové zákazníky. Získejte designovou vizitku, která bude prodávat vaše služby 24 hodin denně. Bez složitostí, rychle, kvalitně a za férovou cenu.",
        cta: "Chci nezávaznou konzultaci",
      },
      why: {
        title: "Co získáte s webem od nás",
        cards: [
          {
            title: "Moderní design a více zákazníků",
            description:
              "Navrhujeme moderní a přehledný UX/UI design, ve kterém se zákazník snadno orientuje. Každý detail je promyšlený tak, aby vzbudil důvěru a přiměl návštěvníka zavolat, napsat nebo přijít.",
          },
          {
            title: "Umíme SEO, GEO a AEO",
            description:
              "Dostaneme vás ve vyhledávačích na přední pozice — na Googlu, Seznamu i ve výsledcích umělé inteligence jako Gemini, Claude nebo ChatGPT. Píšeme čistý kód a optimalizované texty přesně tak, jak to vyžadují vyhledávače i AI. Ukážeme tak vašim zákazníkům cestu přímo k vám.",
          },
          {
            title: "Správu zvládnete sami",
            description:
              "Změna otevírací doby, nová fotka, aktuální cena — to si upravíte sami během dvou minut. Žádné volání programátorovi, žádné čekání.",
          },
          {
            title: "AI, která pracuje místo vás",
            description:
              "Přidáme AI chatbot nebo chytrý formulář, který odpoví zákazníkům i v noci. Vy se ráno probudíte s novými poptávkami v emailu.",
          },
          {
            title: "Texty a fotky? Postaráme se",
            description:
              "Napíšeme vám SEO optimalizované texty, které zaujmou zákazníky a posunou vás výš ve vyhledávání na Googlu i Seznamu. Zařídíme i profesionální fotografie, pokud je budete potřebovat.",
          },
          {
            title: "Doména a hosting",
            description:
              "Pokud nemáte doménu nebo hosting, nechte to na nás. Postaráme se o vše — vy se nemusíte starat o nic technického.",
          },
          {
            title: "Responzivní web",
            description:
              "Každý web děláme plně responzivní — optimalizovaný pro počítač, tablet i mobil a kompatibilní se všemi běžnými prohlížeči. Vypadá skvěle na každém zařízení.",
          },
          {
            title: "Kvalita, rychlost a férová cena",
            description:
              "Díky nejnovějším technologiím pracujeme rychle a efektivně — proto si můžeme dovolit férové ceny bez kompromisů na kvalitě. Váš web dostanete rychleji, než řeknete https.",
          },
          {
            title: "Rezervační systém",
            description:
              "Na váš web integrujeme online rezervační systém, kde si zákazníci sami zarezervují termín. Vy uvidíte jen plný kalendář — žádné telefonáty, žádné e-maily tam a zpět.",
          },
        ],
      },
      process: {
        title: "Bezstarostná spolupráce",
        subtitle:
          "O všechno se postaráme my. Vy nám jen řeknete, co potřebujete, co je pro vás důležité a pak nám schválíte design, který se vám bude líbit.",
        cta: "Sjednat konzultaci",
        steps: [
          {
            title: "Pobavíme se o vašem businessu",
            description:
              "Zavoláme si nebo se sejdeme. Zjistíme, co potřebujete, kdo jsou vaši zákazníci a co by měl web umět. Vaše požadavky doplníme o naše zkušenosti a know-how.",
          },
          {
            title: "Ukážeme vám, jak bude web vypadat",
            description:
              "Před tím, než začneme programovat, dostanete návrh designu ke schválení. Žádná překvapení na konci.",
          },
          {
            title: "Postavíme web na míru a otestujeme ho",
            description: "Naprogramujeme web přesně podle vašich potřeb a otestujeme, že funguje tak, jak má.",
          },
          {
            title: "Spustíme web a naučíme vás, jak s ním zacházet",
            description:
              "Web nasadíme na hosting, spustíme ho a ukážeme vám, jak si ho sami spravovat a měnit na něm informace.",
          },
          {
            title: "Staráme se i potom",
            description:
              "I po spuštění webu s námi můžete počítat. Pokud byste potřebovali s něčím pomoct nebo udělat další úpravy, jsme tu pro vás.",
          },
        ],
      },
      faq: {
        title: "Časté otázky",
        items: [
          {
            question: "Jak rychle web dostanu?",
            answer: "Nový web můžete mít už za týden. U složitějších projektů se vždy předem domluvíme na termínu.",
          },
          {
            question: "Musím si sám řešit fotky a texty?",
            answer:
              "Nemusíte. S obrázky i texty vám pomůžeme — a texty navíc napíšeme kompatibilně se SEO (ale i GEO a AEO) tak, aby vás zákazníci lépe našli na Googlu, Seznamu i pomocí umělé inteligence.",
          },
          {
            question: "Kolik to bude stát?",
            answer:
              "Cena závisí na rozsahu projektu, ale díky moderním technologiím jsme výrazně levnější než klasické agentury. Konkrétní cenovou nabídku dostanete po první konzultaci — zdarma a nezávazně.",
          },
          {
            question: "Postaráte se i o hosting a doménu?",
            answer: "Ano. Pokud hosting nebo doménu nemáte, zařídíme vše za vás. Nemusíte řešit vůbec nic.",
          },
          {
            question: "Budu si web moci sám upravovat?",
            answer:
              "Ano. Uděláme vám web s jednoduchým redakčním systémem, kde změníte texty, fotky nebo ceny sami — bez programátora.",
          },
          {
            question: "Najdou mě zákazníci na Googlu?",
            answer:
              "Na SEO myslíme od prvního řádku kódu. Každý web stavíme technicky čistý, rychlý a optimalizovaný pro vyhledávače — Google i Seznam.",
          },
          {
            question: "Co když mě zákazníci hledají přes ChatGPT nebo Gemini?",
            answer: "I na to jsme připraveni. Optimalizujeme weby i pro AI vyhledávání — takzvané AEO. Jdeme s dobou.",
          },
          {
            question: "Co je to SEO, GEO, AEO?",
            answer:
              "SEO (Search Engine Optimization) je optimalizace webu pro vyhledávače jako Google. Cílem je dostat váš web na přední pozice ve výsledcích vyhledávání, aby vás zákazníci našli dřív než konkurenci.\n\nGEO (Generative Engine Optimization) je novější obor — optimalizace pro AI nástroje jako Gemini, ChatGPT nebo Perplexity. Stále více lidí hledá odpovědi přímo v AI místo Googlu, a GEO zajišťuje, že váš business se zobrazí i tam.\n\nAEO (Answer Engine Optimization) je optimalizace pro přímé odpovědi — kdy Google nebo AI zobrazí odpověď přímo ve výsledcích bez toho, aby uživatel musel klikat na web. Cílem je být tou odpovědí.\n\nZjednodušeně: SEO = Google, GEO = AI vyhledávače, AEO = přímé odpovědi. Všechny tři dohromady zajišťují, že vás zákazníci najdou — ať hledají kdekoliv.",
          },
        ],
      },
      contact: {
        title: "Kontakt",
        subtitle: "Popište nám svůj projekt a my se ozveme. Nezávazná konzultace je zdarma — zavoláme si, nebo se sejdeme osobně.",
      },
    },
    aiAuto: {
      breadcrumb: {
        home: "Domů",
        current: "Automatizace procesů a AI řešení",
      },
      hero: {
        typingPrefix: "Automatizace procesů a ",
        typingWord: { text: "AI řešení", color: "rgb(16, 185, 129)" },
        statement: "Ušetřete čas i peníze.",
        statementSub: "Běžte s dobou, snižte náklady a předběhněte konkurenci.",
        subheadline:
          "Implementujeme do vašeho businessu chytré automatizace a AI agenty, kteří za vás vyřeší rutinní úkoly — odpovídání zákazníkům, třídění emailů, vystavování faktur nebo správu rezervací. Automatizujte rutinu a věnujte se tomu, co vás baví a v čem jste dobří.",
        cta: "Chci nezávaznou konzultaci",
      },
      useCases: {
        label: "USE CASES",
        title: "Co za vás vyřeší automatizace a umělá inteligence?",
        subtitle:
          "Automatizace procesů a AI řešení pomáhají firmám i živnostníkům šetřit čas a peníze. Tady jsou příklady, které nasazujeme u našich klientů.",
        cards: [
          {
            title: "Chatbot pro váš web",
            description:
              "Zákazník přijde na web ve 23:00 a chce znát cenu nebo termín. AI chatbot odpoví okamžitě, zjistí potřeby a ráno vám předá hotovou poptávku do emailu.",
            iconColor: "green",
          },
          {
            title: "Automatické odpovědi na emaily",
            description:
              "AI přečte příchozí email, rozpozná o co jde a buď odpoví automaticky, nebo připraví návrh odpovědi a přesměruje na správnou osobu. Ušetříte hodiny třídění a odepisování každý týden.",
            iconColor: "cyan",
          },
          {
            title: "Voicebot pro váš business",
            description:
              "Neděláme jen chatboty, ale také voiceboty, kteří jsou k nerozeznání od lidského hlasu. Voicebot za vás vyřídí rezervace, objednávky a když si nebude vědět rady, zavolá rovnou vám nebo vám předá zprávu.",
            iconColor: "green",
          },
          {
            title: "Vystavování faktur a dokumentů",
            description:
              "Po dokončení zakázky nebo přijetí objednávky se faktura vystaví automaticky. Žádné ruční opisování, žádné zapomenuté faktury.",
            iconColor: "cyan",
          },
          {
            title: "Správa rezervací a objednávek",
            description:
              "Restaurace, kadeřnictví, autoservis, ordinace — AI přijme rezervaci, potvrdí ji zákazníkovi a zapíše do vašeho kalendáře. Bez jediného telefonátu.",
            iconColor: "green",
          },
          {
            title: "Automatizace sociálních sítí",
            description:
              "Naplánujeme a nastavíme automatické publikování obsahu na vaše sociální sítě. Pravidelná přítomnost online bez každodenní práce.",
            iconColor: "cyan",
          },
          {
            title: "Reporty a přehledy automaticky",
            description:
              "Každý týden nebo měsíc dostanete přehled o tržbách, objednávkách nebo zákaznících — automaticky sestavený, bez ručního exportování dat.",
            iconColor: "green",
          },
          {
            title: "AI agenti",
            description:
              "Vytvoříme vám vlastního AI agenta — chytrého asistenta, který se napojí na vaše systémy a pracuje za vás. Zarezervuje schůzku, odpoví na email, připraví strukturu meetingu nebo vypracuje nabídku. Řeknete mu co potřebujete a on to udělá.",
            iconColor: "cyan",
          },
        ],
      },
      whyUs: {
        title: "Proč svěřit AI automatizaci nám?",
        cards: [
          {
            title: "Rychlé nasazení",
            description: "První automatizaci vám nastavíme během několika dní. Nemusíte měsíce čekat na výsledky.",
          },
          {
            title: "Na míru vašemu businessu",
            description:
              "Žádná šablonová řešení. Každou automatizaci navrhujeme přesně podle vašich procesů a potřeb.",
          },
          {
            title: "Naučíme vás s tím",
            description:
              "Automatizace vám předáme s jasným návodem. Budete vědět, co se děje, a v jednoduchých věcech si poradíte sami.",
          },
          {
            title: "Jsme tu i potom",
            description:
              "Automatizace není produkt, který koupíte a tím to končí. Monitorujeme, vylepšujeme a jsme tu, když něco potřebujete změnit.",
          },
        ],
      },
      process: {
        title: "Bezstarostná spolupráce",
        subtitle:
          "Nemusíte rozumět AI ani technologiím. Stačí nám říct, co vás zdržuje a co byste chtěli dělat jinak — zbytek zařídíme my.",
        cta: "Sjednat konzultaci",
        steps: [
          {
            title: "Zjistíme, kde ztrácíte čas",
            description:
              "Probereme vaše každodenní procesy a společně najdeme místa, kde automatizace nebo AI řešení ušetří nejvíce času a peněz.",
          },
          {
            title: "Navrhneme řešení",
            description:
              "Připravíme konkrétní návrh automatizace nebo AI řešení — co bude dělat, jak to bude fungovat a co od toho můžete čekat.",
          },
          {
            title: "Nastavíme a otestujeme",
            description:
              "Řešení naprogramujeme, napojíme na vaše nástroje a důkladně otestujeme, než ji spustíme naostro.",
          },
          { title: "Spustíme a předáme", description: "Nasadíme řešení do provozu a naučíme vás s tím zacházet." },
          {
            title: "Staráme se i potom",
            description: "Sledujeme, jestli vše běží jak má. Pokud chcete něco přidat nebo upravit, jsme tu pro vás.",
          },
        ],
      },
      faq: {
        label: "ČASTÉ OTÁZKY",
        title: "Časté otázky",
        items: [
          {
            question: "Musím rozumět AI nebo programování?",
            answer:
              "Vůbec ne. Od vás potřebujeme jen popsat, co děláte a co byste chtěli automatizovat nebo přenést na AI. Technickou část zařídíme kompletně my.",
          },
          {
            question: "Kolik taková automatizace stojí?",
            answer:
              "Záleží na složitosti. Jednoduchý AI chatbot nebo emailová automatizace jsou výrazně levnější, než si většina lidí myslí. Konkrétní cenovou nabídku dostanete po první konzultaci — zdarma a nezávazně.",
          },
          {
            question: "S jakými nástroji pracujete?",
            answer:
              "Pracujeme s nejrozšířenějšími automatizačními a AI nástroji — Make, Zapier, n8n, OpenAI, Claude, Gemini a dalšími. Napojíme se na systémy, které už používáte.",
          },
          {
            question: "Jak dlouho trvá nastavení?",
            answer:
              "Jednoduché automatizace nebo AI agenty zvládneme za pár dní. Složitější projekty prodiskutujeme a domluvíme se na reálném termínu předem.",
          },
          {
            question: "Půjde to napojit na můj web nebo e-shop?",
            answer:
              "Ano, ve většině případů bez problému. Pracujeme se Shoptetem, WordPressem, Shopify, vlastními weby i dalšími platformami.",
          },
          {
            question: "Mohu automatizaci později rozšířit?",
            answer:
              "Ano, a doporučujeme začít jednoduše. Postupně přidáváme další automatizace, jak roste vaše firma a vaše potřeby.",
          },
        ],
      },
      contact: {
        title: "Kontakt",
        subtitle: "Pojďme to probrat. Nezávazná konzultace je zdarma. Zavoláme si, napíšeme, nebo se sejdeme osobně.",
      },
    },
    appDev: {
      breadcrumb: {
        home: "Domů",
        current: "Tvorba aplikací",
      },
      hero: {
        typingPrefix: "Tvoříme ",
        typingWord: { text: "aplikace", color: "#8B5CF6" },
        statement: "Máte nápad nebo businessový problém? Uděláme z toho fungující aplikaci.",
        subheadline:
          "Vyvíjíme webové, mobilní i progresivní aplikace (PWA) na míru, které šetří čas, zjednodušují procesy a reálně pomáhají vašemu businessu růst. iOS, Android, web — bez kompromisů.",
        cta: "Chci nezávaznou konzultaci",
      },
      offer: {
        title: "Co umíme vytvořit?",
        subtitle: "Pracujeme s nejmodernějšími technologiemi pro všechny platformy.",
        cards: [
          {
            title: "PWA — progresivní webové aplikace",
            description:
              "Běží na všech zařízeních přes prohlížeč, ale chová se jako nativní aplikace. Žádné stahování z App Store, snadné aktualizace a výrazně nižší náklady na vývoj.",
          },
          {
            title: "iOS aplikace",
            description:
              "Nativní aplikace pro iPhone a iPad. Rychlé, intuitivní a přesně podle Apple design guidelines — vaši zákazníci si je oblíbí okamžitě.",
          },
          {
            title: "Android aplikace",
            description:
              "Aplikace pro miliardy Android zařízení po celém světě. Optimalizované pro různé velikosti obrazovek s moderním Material Design a intuitivním UX/UI.",
          },
          {
            title: "Multiplatformní a hybrid aplikace",
            description:
              "Jedna aplikace pro iOS, Android i web zároveň. Ušetříte až 60 % nákladů oproti vývoji tří samostatných aplikací.",
          },
          {
            title: "Interní firemní aplikace",
            description:
              "Docházkový systém, správa skladu, CRM, schvalovací workflow nebo firemní portál — vyvineme aplikaci přesně pro vaše interní potřeby.",
          },
        ],
      },
      whyUs: {
        title: "Proč svěřit vývoj aplikace nám?",
        cards: [
          {
            title: "Od nápadu po App Store",
            description:
              "Postaráme se o celý proces — od návrhu UX/UI přes vývoj až po publikaci na Google Play nebo App Store. Nemusíte řešit vůbec nic.",
          },
          {
            title: "Rychlý vývoj, reálné termíny",
            description:
              "Díky moderním technologiím a zkušenostem dodáváme aplikace rychleji a levněji než klasické agentury — bez kompromisů na kvalitě.",
          },
          {
            title: "Aplikace, která roste s vámi",
            description:
              "Stavíme škálovatelné aplikace od začátku. Když přidáte nové funkce nebo začnete obsloužit tisíce uživatelů, aplikace to zvládne bez problémů.",
          },
          {
            title: "Podpora i po spuštění",
            description:
              "Spuštění aplikace není konec spolupráce. Monitorujeme, aktualizujeme a přidáváme funkce podle vašich potřeb.",
          },
        ],
      },
      process: {
        title: "Bezstarostný vývoj",
        subtitle:
          "Nemusíte rozumět technologiím. Stačí nám říct, jaký máte nápad, případně co má aplikace dělat a komu je určena — zbytek zařídíme my.",
        cta: "Sjednat konzultaci",
        steps: [
          {
            title: "Pochopíme váš nápad",
            description:
              "Probereme, co má aplikace dělat, kdo ji bude používat a jaký problém má řešit. Přidáme své know-how a zkušenosti.",
          },
          {
            title: "Navrhneme design",
            description:
              "Vytvoříme interaktivní prototyp — uvidíte, jak bude aplikace vypadat a fungovat, ještě před programováním. Schválíme si design i funkcionality.",
          },
          {
            title: "Naprogramujeme a otestujeme",
            description:
              "Postavíme aplikaci na míru a důkladně ji otestujeme na různých zařízeních a prohlížečích. Žádná překvapení po spuštění.",
          },
          {
            title: "Publikujeme ji",
            description: "Postaráme se o nasazení — ať už na Google Play, App Store nebo jako PWA na vašem webu.",
          },
          {
            title: "Staráme se i potom",
            description:
              "Sledujeme výkon aplikace, řešíme případné problémy a přidáváme nové funkce podle toho, jak váš byznys roste.",
          },
        ],
      },
      faq: {
        title: "Časté otázky",
        items: [
          {
            question: "Jaký je rozdíl mezi PWA a nativní aplikací?",
            answer:
              "PWA funguje přes prohlížeč na všech zařízeních — nepotřebuje stahování z App Store. Nativní aplikace je optimalizovaná přímo pro iOS nebo Android a má přístup k více funkcím telefonu. Poradíme vám, co je pro váš případ lepší.",
          },
          {
            question: "Kolik stojí vývoj aplikace?",
            answer:
              "Záleží na složitosti a rozsahu. Jednoduchá PWA bude výrazně levnější než komplexní nativní aplikace. Konkrétní cenovou nabídku dostanete po první konzultaci — zdarma a nezávazně.",
          },
          {
            question: "Jak dlouho trvá vývoj?",
            answer:
              "Jednoduchá aplikace může být hotová za 2–6 týdnů. Složitější projekty prodiskutujeme a domluvíme se na reálném termínu předem.",
          },
          {
            question: "Pomůžete mi s publikací na App Store a Google Play?",
            answer:
              "Ano, celý proces publikace zařídíme za vás — včetně vytvoření vývojářských účtů, pokud je ještě nemáte.",
          },
          {
            question: "Půjde aplikace napojit na můj web nebo e-shop?",
            answer:
              "Ano, ve většině případů bez problému. Napojíme na WordPress, Shoptet, Shopify nebo vlastní backend.",
          },
          {
            question: "Mohu aplikaci později rozšířit o nové funkce?",
            answer:
              "Ano, a stavíme aplikace přesně tak, aby se daly snadno rozšiřovat. Můžeme začít s tím nejdůležitějším a postupně přidávat další funkce.",
          },
          {
            question: "Bude aplikace fungovat offline?",
            answer:
              "Ano — PWA i nativní aplikace umíme navrhnout tak, aby základní funkce fungovaly i bez připojení k internetu. Konkrétní možnosti probereme podle vašich požadavků.",
          },
        ],
      },
      contact: {
        subtitle: "Pojďme to probrat. Nezávazná konzultace je zdarma. Zavoláme si, napíšeme — nebo se sejdeme osobně.",
      },
    },
    aiTraining: {
      breadcrumb: {
        home: "Domů",
        current: "AI školení a workshopy",
      },
      hero: {
        typingPrefix: "Naučíme vás ",
        typingWord: { text: "pracovat s AI.", color: "#F97316" },
        statement:
          "Praktické workshopy přímo ve vaší firmě. Žádná teorie navíc — jen postupy, které váš tým použije hned druhý den.",
        subheadline:
          "Naučíme váš tým efektivně využívat AI modely jako jsou Claude, Gemini nebo ChatGPT, ale také další AI nástroje týkající se vašeho businessu. To vše na konkrétních příkladech z praxe. Ušetřete hodiny práce denně a získejte náskok před konkurencí.",
        cta: "Chci nezávaznou konzultaci",
      },
      offer: {
        title: "Co vás naučíme?",
        subtitle: "Každé školení přizpůsobujeme přímo vašemu oboru a potřebám.",
        cards: [
          {
            title: "ChatGPT, Claude, Gemini v praxi",
            description:
              "Naučíme vás psát prompty, které fungují. Využití AI pro tvorbu textů, emailů, nabídek nebo zákaznické komunikace — přímo na příkladech z vašeho byznysu.",
          },
          {
            title: "AI pro tvorbu obsahu",
            description:
              "Generování obrázků, videí a marketingového obsahu pomocí AI nástrojů jako Midjourney, DALL-E nebo Sora. Sociální sítě, reklamy a prezentace za zlomek času.",
          },
          {
            title: "AI vyhledávání a research",
            description:
              "Jak využít AI pro rychlý průzkum trhu, analýzu konkurence nebo přípravu podkladů. Ušetřete hodiny manuální práce.",
          },
          {
            title: "Automatizace každodenních úkolů",
            description:
              "Praktické nástroje pro automatizaci opakujících se úkolů — od třídění emailů po správu dat, tvorbu reportů a plánování schůzek.",
          },
          {
            title: "AI pro analýzu dat",
            description:
              "Jak využít AI pro efektivní analýzu dat, tvorbu přehledů a rozhodování na základě čísel — bez znalosti programování.",
          },
          {
            title: "Bezpečné a etické využití AI",
            description:
              "Co smíte a nesmíte svěřit AI. Evropský AI Act, ochrana firemních dat, GDPR a praktická pravidla pro bezpečné používání AI ve firmě.",
          },
        ],
      },
      audience: {
        title: "Pro koho školení děláme?",
        cards: [
          {
            title: "Firmy a jejich týmy",
            description:
              "Workshop pro celý tým nebo vybrané oddělení. Přizpůsobíme obsah přesně vaší branži a každodenním procesům.",
          },
          {
            title: "Manažery a majitele firem",
            description: "Jak AI využít strategicky — pro lepší rozhodování, efektivnější plánování a získání náskoku před konkurencí.",
          },
          {
            title: "Jednotlivce",
            description:
              "Chcete se sami naučit pracovat s AI efektivně? Individuální školení přizpůsobené vašim cílům a tempu.",
          },
          {
            title: "Živnostníky",
            description:
              "AI jako váš osobní asistent. Ušetřete hodiny týdně na administrativě, komunikaci a tvorbě obsahu.",
          },
        ],
      },
      process: {
        title: "Školení šité na míru",
        subtitle:
          "Žádné obecné prezentace o tom, co AI umí. Zjistíme, co děláte, přijedeme k vám a naučíme vás přesně to, co použijete hned.",
        cta: "Sjednat konzultaci",
        steps: [
          {
            title: "Zjistíme vaše potřeby",
            description:
              "Zavoláme si nebo se sejdeme. Zjistíme, co váš tým dělá, kde ztrácí čas a co by AI mohla zjednodušit.",
          },
          {
            title: "Připravíme obsah na míru",
            description:
              "Sestavíme program školení přesně pro váš obor a tým — s konkrétními příklady z vaší praxe, ne z učebnice.",
          },
          {
            title: "Přijedeme k vám",
            description:
              "Workshop provedeme přímo u vás ve firmě nebo online přes video hovor. Prakticky, interaktivně, bez zbytečné teorie.",
          },
          {
            title: "Předáme materiály",
            description: "Po školení dostanete přehledné podklady a návody, ke kterým se tým může kdykoli vrátit.",
          },
          {
            title: "Jsme tu i potom",
            description:
              "Máte otázky po školení? Ozvěte se. Rádi pomůžeme, když narazíte na konkrétní problém v praxi.",
          },
        ],
      },
      faq: {
        title: "Časté otázky",
        items: [
          {
            question: "Musí náš tým umět pracovat s počítačem?",
            answer:
              "Stačí základní práce s počítačem a internetem. Vše ostatní vysvětlíme od základů, srozumitelně a bez zbytečného technického žargonu.",
          },
          {
            question: "Kolik lidí může školení absolvovat najednou?",
            answer:
              "Ideálně 5–20 lidí, aby byl prostor na praktické cvičení a dotazy. Pro větší skupiny školení rozdělíme nebo přizpůsobíme formát.",
          },
          {
            question: "Jak dlouho školení trvá?",
            answer:
              "Základní workshop trvá půl dne (3–4 hodiny). Komplexnější školení přizpůsobíme rozsahu — klidně i celodenní nebo vícedenní program.",
          },
          {
            question: "Přijedete k nám, nebo je školení online?",
            answer:
              "Obojí. Preferujeme osobní setkání přímo u vás, ale zvládneme i plnohodnotné online školení přes video hovor.",
          },
          {
            question: "Kolik školení stojí?",
            answer: "Záleží na rozsahu a počtu účastníků. Konkrétní cenovou nabídku dostanete po první konzultaci — zdarma a nezávazně.",
          },
          {
            question: "Dostaneme nějaké materiály?",
            answer: "Ano, po každém školení předáme přehledné podklady a návody, ke kterým se tým může kdykoli vrátit.",
          },
          {
            question: "Lze školení přizpůsobit našemu oboru?",
            answer:
              "Ano, a je to základ naší práce. Každé školení stavíme na konkrétních příkladech z vašeho byznysu — ne na obecných ukázkách.",
          },
        ],
      },
      contact: {
        subtitle: "Pojďme to probrat. Nezávazná konzultace je zdarma. Zavoláme si, napíšeme — nebo se sejdeme osobně.",
      },
    },
  },
  en: {
    nav: {
      logo: "AiLum Solutions",
      items: [
        { label: "Services", href: "#sluzby" },
        { label: "About", href: "/#o-nas" },
        { label: "Collaboration", href: "/#spoluprace" },
        { label: "Contact", href: "/#kontakt" },
      ],
      languageSwitch: "CZ",
    },
    hero: {
      typingPrefix: "We build ",
      typingWords: [
        { text: "Modern websites", color: "#00d4ff" },
        { text: "Applications", color: "#ff6b35" },
        { text: "Automations", color: "#7c86ff" },
        { text: "AI agents", color: "#10b981" },
        { text: "AI Workshops", color: "#F97316" },
      ],
      resultLine: "Digital solutions that drive your business forward",
      headline: ["Modern websites & apps.", "AI Automation.", "Digital solutions that drive your business forward"],
      subheadline:
        "We understand business and technology. We build websites, applications and AI automations for companies that want to grow. We'll help you get more customers, streamline processes and reduce costs.",
      cta: {
        primary: "Get a free consultation",
        secondary: "Contact us",
      },
      trustIndicators: [
        "Fast and responsive websites",
        "Modern web and mobile apps",
        "SEO optimization",
        "AI consulting, automation & training",
      ],
      scrollIndicator: "Why choose us",
    },
    mobileMenu: {
      open: "Open menu",
      close: "Close menu",
    },
    services: {
      title: "Services",
      subtitle: "Tell us about your project, idea or problem and let us guide you",
      items: [
        {
          id: "weby",
          title: "Website Development",
          description:
            "We build modern, fast custom websites that impress at first sight and bring in customers. Responsive design, SEO optimization and easy content management — everything your website needs to work for your business.",
          cta: "Learn more →",
        },
        {
          id: "ai-automatizace",
          title: "Automation & AI Solutions",
          description:
            "We connect your systems, automate repetitive processes and build custom AI chatbots, voicebots or AI agents. Save dozens of hours per month and reduce operational costs — let AI work for you.",
          cta: "Learn more →",
        },
        {
          id: "aplikace",
          title: "Application Development",
          description:
            "We develop custom web and mobile applications — from MVPs for startups to complex enterprise solutions. Get scalable software that solves your specific business problem, saves time and drives company growth.",
          cta: "Learn more →",
        },
        {
          id: "ai-skoleni",
          title: "AI Training & Workshops",
          description:
            "We'll run a workshop at your company and train your team to effectively use AI tools on real examples from your practice. We'll teach you to work with ChatGPT, Claude, Gemini and other AI tools tailored to your needs, so they save you hours of work every day and boost productivity across your entire company.",
          cta: "Learn more →",
        },
      ],
    },
    about: {
      title: "About Us",
      blockLabels: ["Web & App Development", "Custom AI & Automation", "Why choose AiLum Solutions"],
      description: [
        "We build websites and applications with an eye for design and attention to detail. We combine technical expertise with UX/UI skills — the result is websites and apps that are fast, clear and pleasant to use. We want your website to bring in customers and build trust, not just look good.",
        "AI isn't just a buzzword — it's a tool that can genuinely save you time and money. We have experience implementing automations, AI agents, and training teams in practice, so we know what works and what doesn't. We'll help you find the areas where it makes the most sense and set everything up tailored to your business.",
        "You're not just another client to us — we take your business seriously and want to be your partner. We have experience in business, marketing, technology, and law, so we understand your challenges from multiple angles and can design solutions that truly work.\n\nWe have an eye for detail. It's not just about a nice website or app — we want to create a tool that brings you customers, saves time, and strengthens competitiveness. And we don't disappear after launch — we stay with you long-term and help whenever needed.\n\nWe work fast and deliver quality for fair price. Contact us and see it for yourself, the consultation is for free.",
      ],
    },
    process: {
      title: "How we collaborate",
      subtitle:
        "We'll meet, understand your business, and design the optimal solution — then we'll take care of the rest. We'll keep you informed of progress throughout, so you'll always know what's happening. You'll get the result on time and exactly as you envisioned.",
      cta: "Book a consultation",
      steps: [
        {
          number: 1,
          title: "We understand your business and\u00A0your requirements",
          desc: "In a meeting, we discuss your business and find out how we can help.",
        },
        {
          number: 2,
          title: "We do the analysis",
          desc: "We analyze your requirements and your problem, and come up with the right solution.",
        },
        {
          number: 3,
          title: "We create the design",
          desc: "We create the product design, show it to you, and get your approval.",
        },
        {
          number: 4,
          title: "We develop",
          desc: "We bring the design to life and develop the final product — website, app, automation, or AI agent.",
        },
        {
          number: 5,
          title: "We test",
          desc: "We thoroughly test the product from all angles to make sure it works as it should.",
        },
        {
          number: 6,
          title: "Production deployment",
          desc: "We deploy the product to production so you can start using it.",
        },
        {
          number: 7,
          title: "Ongoing support",
          desc: "It doesn't end there for us. If you need help afterwards, we're here for you.",
        },
      ],
    },
    contact: {
      title: "Contact",
      subtitle: "Tell us about your project and we'll get back to you. Free consultation, no strings attached.",
      form: {
        name: "Name",
        email: "Email",
        phone: "Phone",
        message: "Message",
        submit: "Send message",
        submitting: "...",
        gdpr: "By submitting this form, you agree to the processing of your personal data for the purpose of handling your inquiry. More information:",
        gdprLink: "Privacy Policy",
      },
      success: "Thank you for your message! We'll get back to you soon.",
      error: "Failed to send your message. Please try again.",
      details: {
        name: "Lukáš Muladi",
        email: "info@ailum.cz",
        phone: "+420 773 485 406",
        ico: "ID: 09302794",
      },
    },
    footer: {
      copyright: "© 2025 AiLum Solutions. All rights reserved.",
      tagline: "Web & app development. AI automation & training. Digital solutions for businesses.",
      address: "Address: Paskovská 229, Krmelín, 739 24",
      registry: "Registered in the Trade Register of the Municipality of Frýdek-Místek",
      cookieSettings: "Cookie Settings",
      privacyPolicy: "Privacy Policy",
    },
    webDev: {
      breadcrumb: {
        home: "Home",
        current: "Web Development",
      },
      hero: {
        typingPrefix: "We build ",
        typingWord: { text: "modern", color: "#00d4ff" },
        typingSuffix: " websites.",
        subheadline:
          "We'll build you a modern website that impresses at first sight and brings in new customers. Get a design that sells your services 24 hours a day. No complications, fast delivery, quality work and a fair price.",
        cta: "Get a free consultation",
      },
      why: {
        title: "What you get with a website from us",
        cards: [
          {
            title: "Modern design and more customers",
            description:
              "We design modern, clean UX/UI that's easy for customers to navigate. Every detail is crafted to build trust and encourage visitors to call, write or walk in.",
          },
          {
            title: "We know SEO, GEO, and AEO",
            description:
              "We'll get you to the top of search results — on Google, Seznam and in AI search engines like Gemini, Claude or ChatGPT. We write clean code and optimized content exactly the way search engines and AI require. We'll show your customers the way straight to you.",
          },
          {
            title: "You can manage it yourself",
            description:
              "Changing opening hours, adding a photo, updating a price — you can do it yourself in two minutes. No calling a developer, no waiting.",
          },
          {
            title: "AI that works for you",
            description:
              "We'll add an AI chatbot or smart form that responds to customers even at night. You'll wake up with new inquiries in your inbox.",
          },
          {
            title: "Copy and photos? We've got it",
            description:
              "We'll write SEO-optimized copy that engages customers and boosts your search rankings on Google and Seznam. We'll also arrange professional photography if you need it.",
          },
          {
            title: "Domain and hosting",
            description:
              "If you don't have a domain or hosting, leave it to us. We'll take care of everything — you don't have to worry about anything technical.",
          },
          {
            title: "Responsive website",
            description:
              "Every website we build is fully responsive — optimized for desktop, tablet and mobile and compatible with all major browsers. It looks great on every device.",
          },
          {
            title: "Quality, speed, and fair pricing",
            description:
              "Thanks to the latest technologies, we work fast and efficiently — that's why we can offer fair prices without compromising on quality. You'll get your website faster than you can say https.",
          },
          {
            title: "Booking system",
            description:
              "We'll integrate an online booking system into your website where customers book appointments themselves. All you'll see is a full calendar — no phone calls, no back-and-forth emails.",
          },
        ],
      },
      process: {
        title: "Hassle-free collaboration",
        subtitle:
          "We take care of everything. You just tell us what you need, what's important to you, and then approve the design you like.",
        cta: "Book a consultation",
        steps: [
          {
            title: "We talk about your business",
            description:
              "We'll hop on a call or meet in person. We'll find out what you need, who your customers are and what your website should do. We'll combine your requirements with our experience and know-how.",
          },
          {
            title: "We show you what the website will look like",
            description:
              "Before we start coding, you'll receive a design proposal for approval. No surprises at the end.",
          },
          {
            title: "We build and test the website",
            description: "We code the website exactly to your needs and test that everything works as it should.",
          },
          {
            title: "We launch the website and show you how to use it",
            description:
              "We deploy the website to hosting, launch it, and show you how to manage and update information on it yourself.",
          },
          {
            title: "We're here even after launch",
            description:
              "You can count on us even after the website launches. If you need help with anything or want further changes, we're here for you.",
          },
        ],
      },
      faq: {
        title: "Frequently asked questions",
        items: [
          {
            question: "How quickly will I get the website?",
            answer:
              "You can have a new website in as little as a week. For more complex projects, we always agree on a deadline in advance.",
          },
          {
            question: "Do I need to handle photos and copy myself?",
            answer:
              "You don't. We'll help with both images and copy — and we'll write the copy to be SEO-compatible (as well as GEO and AEO) so customers can find you more easily on Google, Bing, and through AI.",
          },
          {
            question: "How much will it cost?",
            answer:
              "The price depends on the scope of the project, but thanks to modern technologies we're significantly cheaper than traditional agencies. You'll get a specific quote after your first consultation — free and with no obligation.",
          },
          {
            question: "Will you handle hosting and domain?",
            answer:
              "Yes. If you don't have hosting or a domain, we'll arrange everything for you. You don't need to worry about anything.",
          },
          {
            question: "Will I be able to edit the website myself?",
            answer:
              "Yes. We'll build your website with a simple CMS where you can change text, photos, or prices yourself — without a developer.",
          },
          {
            question: "Will customers find me on Google?",
            answer:
              "We think about SEO from the very first line of code. Every website we build is technically clean, fast and optimized for search engines — Google and Seznam.",
          },
          {
            question: "What if customers search via ChatGPT or Gemini?",
            answer:
              "We're prepared for that too. We optimize websites for AI search — so-called AEO. We keep up with the times.",
          },
          {
            question: "What is SEO, GEO, AEO?",
            answer:
              "SEO (Search Engine Optimization) is website optimization for search engines like Google. The goal is to get your website to the top of search results so customers find you before the competition.\n\nGEO (Generative Engine Optimization) is a newer field — optimization for AI tools like Gemini, ChatGPT, or Perplexity. More and more people are looking for answers directly in AI instead of Google, and GEO ensures your business shows up there too.\n\nAEO (Answer Engine Optimization) is optimization for direct answers — when Google or AI displays an answer directly in results without the user having to click on a website. The goal is to be that answer.\n\nIn simple terms: SEO = Google, GEO = AI search engines, AEO = direct answers. All three together ensure customers find you — no matter where they search.",
          },
        ],
      },
      contact: {
        title: "Contact",
        subtitle: "Tell us about your project and we'll get back to you. Free consultation, no strings attached — we can call, or meet in person.",
      },
    },
    aiAuto: {
      breadcrumb: {
        home: "Home",
        current: "Process Automation & AI Solutions",
      },
      hero: {
        typingPrefix: "Process Automation & ",
        typingWord: { text: "AI Solutions", color: "rgb(16, 185, 129)" },
        statement: "Save time and money.",
        statementSub: "Stay ahead, cut costs, and outpace your competition.",
        subheadline:
          "We implement smart automations and AI agents into your business that handle routine tasks for you — responding to customers, sorting emails, issuing invoices or managing bookings. Automate the routine and focus on what you enjoy and do best.",
        cta: "Get a free consultation",
      },
      useCases: {
        label: "USE CASES",
        title: "What will automation and artificial intelligence handle for you?",
        subtitle:
          "Process automation and AI solutions help businesses and freelancers save time and money. Here are examples we deploy for our clients.",
        cards: [
          {
            title: "Chatbot for your website",
            description:
              "A customer visits your website at 11 PM and wants to know the price or availability. An AI chatbot responds instantly, identifies their needs and delivers a ready-made inquiry to your inbox by morning.",
            iconColor: "green" as const,
          },
          {
            title: "Automatic email replies",
            description:
              "AI reads incoming emails, identifies the topic and either replies automatically or prepares a draft response and forwards it to the right person. You'll save hours of sorting and replying every week.",
            iconColor: "cyan" as const,
          },
          {
            title: "Voicebot for your business",
            description:
              "We don't just build chatbots — we also build voicebots indistinguishable from a human voice. The voicebot handles reservations, orders, and when it's unsure, it calls you directly or passes on the message.",
            iconColor: "green" as const,
          },
          {
            title: "Invoice and document generation",
            description:
              "After completing a job or receiving an order, the invoice is generated automatically. No manual copying, no forgotten invoices.",
            iconColor: "cyan" as const,
          },
          {
            title: "Booking and order management",
            description:
              "Restaurants, hair salons, car services, clinics — AI accepts a booking, confirms it with the customer and adds it to your calendar. Without a single phone call.",
            iconColor: "green" as const,
          },
          {
            title: "Social media automation",
            description:
              "We plan and set up automatic content publishing on your social media. Regular online presence without daily work.",
            iconColor: "cyan" as const,
          },
          {
            title: "Reports and dashboards automatically",
            description:
              "Every week or month you receive a summary of revenue, orders, or customers — automatically compiled, without manual data exports.",
            iconColor: "green" as const,
          },
          {
            title: "AI agents",
            description:
              "We'll build you a custom AI agent — a smart assistant that connects to your systems and works for you. It books meetings, replies to emails, prepares meeting agendas or drafts proposals. You tell it what you need and it gets it done.",
            iconColor: "cyan" as const,
          },
        ],
      },
      whyUs: {
        title: "Why trust AI automation to us?",
        cards: [
          {
            title: "Fast deployment",
            description: "We'll set up your first automation within a few days. No months of waiting for results.",
          },
          {
            title: "Tailored to your business",
            description:
              "No cookie-cutter solutions. We design every automation specifically around your processes and needs.",
          },
          {
            title: "We'll teach you",
            description:
              "We hand over automations with clear instructions. You'll know what's happening, and you'll handle simple things on your own.",
          },
          {
            title: "We're here afterwards too",
            description:
              "Automation isn't a product you buy and that's it. We monitor, improve, and we're here when you need changes.",
          },
        ],
      },
      process: {
        title: "Hassle-free collaboration",
        subtitle:
          "You don't need to understand AI or technology. Just tell us what slows you down and what you'd like to do differently — we'll handle the rest.",
        cta: "Book a consultation",
        steps: [
          {
            title: "We find where you're losing time",
            description:
              "We go through your daily processes and together identify where automation or AI solutions can save you the most time and money.",
          },
          {
            title: "We design the solution",
            description:
              "We prepare a concrete automation or AI solution proposal — what it will do, how it will work, and what you can expect.",
          },
          {
            title: "We set up and test",
            description: "We program the solution, connect it to your tools, and thoroughly test it before going live.",
          },
          { title: "We launch and hand over", description: "We deploy the solution and teach you how to use it." },
          {
            title: "We take care of it afterwards",
            description:
              "We monitor that everything runs smoothly. If you want to add or adjust something, we're here for you.",
          },
        ],
      },
      faq: {
        label: "FAQ",
        title: "Frequently asked questions",
        items: [
          {
            question: "Do I need to understand AI or programming?",
            answer:
              "Not at all. We just need you to describe what you do and what you'd like to automate or hand off to AI. We handle the technical side completely.",
          },
          {
            question: "How much does automation cost?",
            answer:
              "It depends on the complexity. A simple AI chatbot or email automation is significantly cheaper than most people think. You'll get a specific quote after your first consultation — free and with no obligation.",
          },
          {
            question: "What tools do you work with?",
            answer:
              "We work with the most widely used automation and AI tools — Make, Zapier, n8n, OpenAI, Claude, Gemini and more. We connect to the systems you already use.",
          },
          {
            question: "How long does setup take?",
            answer:
              "Simple automations or AI agents take just a few days. For complex projects, we discuss and agree on a realistic timeline upfront.",
          },
          {
            question: "Can it connect to my website or e-shop?",
            answer:
              "Yes, in most cases without any issues. We work with Shoptet, WordPress, Shopify, custom websites, and other platforms.",
          },
          {
            question: "Can I expand the automation later?",
            answer:
              "Yes, and we recommend starting simple. We gradually add more automations as your business and needs grow.",
          },
        ],
      },
      contact: {
        title: "Contact",
        subtitle: "Let's talk about it. A free consultation is no-obligation. We can call, write, or meet in person.",
      },
    },
    appDev: {
      breadcrumb: {
        home: "Home",
        current: "Application Development",
      },
      hero: {
        typingPrefix: "We build ",
        typingWord: { text: "applications", color: "#8B5CF6" },
        statement: "Got an idea or a business problem? We'll turn it into a working application.",
        subheadline:
          "We develop custom web, mobile and progressive web applications (PWA) that save time, simplify processes and help your business grow. iOS, Android, web — no compromises.",
        cta: "Get a free consultation",
      },
      offer: {
        title: "What can we build?",
        subtitle: "We work with cutting-edge technologies for all platforms.",
        cards: [
          {
            title: "PWA — Progressive Web Apps",
            description:
              "Runs on all devices through the browser but behaves like a native app. No App Store downloads, easy updates and significantly lower development costs.",
          },
          {
            title: "iOS Applications",
            description:
              "Native apps for iPhone and iPad. Fast, intuitive, and following Apple design guidelines — your customers will love them instantly.",
          },
          {
            title: "Android Applications",
            description:
              "Applications for billions of Android devices worldwide. Optimized for various screen sizes with modern Material Design and intuitive UX/UI.",
          },
          {
            title: "Cross-platform & Hybrid Apps",
            description:
              "One application for iOS, Android and web at the same time. Save up to 60% of costs compared to developing three separate applications.",
          },
          {
            title: "Internal Business Applications",
            description:
              "Attendance systems, inventory management, CRM, approval workflows or company portals — we'll develop an application tailored to your internal needs.",
          },
        ],
      },
      whyUs: {
        title: "Why trust app development to us?",
        cards: [
          {
            title: "From idea to App Store",
            description:
              "We take care of the entire process — from UX/UI design through development to publishing on Google Play or App Store. You don't need to handle anything.",
          },
          {
            title: "Fast development, realistic deadlines",
            description:
              "Thanks to modern technologies and experience, we deliver applications faster and cheaper than traditional agencies — without compromising on quality.",
          },
          {
            title: "An app that grows with you",
            description:
              "We build scalable applications from the start. When you add new features or start serving thousands of users, the app handles it without issues.",
          },
          {
            title: "Support even after launch",
            description:
              "Launching the app isn't the end of our collaboration. We monitor, update, and add features based on your needs.",
          },
        ],
      },
      process: {
        title: "Hassle-free development",
        subtitle:
          "You don't need to understand technology. Just tell us your idea, what the app should do and who it's for — we'll handle the rest.",
        cta: "Book a consultation",
        steps: [
          {
            title: "We understand your idea",
            description:
              "We discuss what the app should do, who will use it, and what problem it should solve. We add our know-how and experience.",
          },
          {
            title: "We design it",
            description:
              "We'll create an interactive prototype — you'll see how the app will look and work before any coding begins. We'll agree on the design and functionality together.",
          },
          {
            title: "We build and test",
            description:
              "We build the app to spec and thoroughly test it on different devices and browsers. No surprises after launch.",
          },
          {
            title: "We publish it",
            description: "We handle deployment — whether on Google Play, App Store, or as a PWA on your website.",
          },
          {
            title: "We take care of it afterwards",
            description: "We monitor app performance, fix any issues, and add new features as your business grows.",
          },
        ],
      },
      faq: {
        title: "Frequently asked questions",
        items: [
          {
            question: "What's the difference between PWA and native app?",
            answer:
              "PWA works through a browser on all devices — no App Store download needed. A native app is optimized directly for iOS or Android and has access to more phone features. We'll advise you on what's better for your case.",
          },
          {
            question: "How much does app development cost?",
            answer:
              "It depends on the complexity and scope. A simple PWA will be significantly cheaper than a complex native application. You'll get a specific quote after your first consultation — free and with no obligation.",
          },
          {
            question: "How long does development take?",
            answer:
              "A simple application can be ready in 2–6 weeks. For more complex projects, we'll discuss and agree on a realistic timeline upfront.",
          },
          {
            question: "Will you help with App Store and Google Play publishing?",
            answer:
              "Yes, we handle the entire publishing process for you — including creating developer accounts if you don't have them yet.",
          },
          {
            question: "Can the app connect to my website or e-shop?",
            answer:
              "Yes, in most cases without any issues. We connect to WordPress, Shoptet, Shopify, or custom backends.",
          },
          {
            question: "Can I add new features later?",
            answer:
              "Yes, and we build apps specifically so they can be easily extended. We can start with the essentials and gradually add more features.",
          },
          {
            question: "Will the app work offline?",
            answer:
              "Yes — we can design both PWA and native applications so that basic features work without an internet connection. We'll discuss the specific options based on your requirements.",
          },
        ],
      },
      contact: {
        subtitle: "Let's talk about it. A free consultation is no-obligation. We can call, write — or meet in person.",
      },
    },
    aiTraining: {
      breadcrumb: {
        home: "Home",
        current: "AI Training & Workshops",
      },
      hero: {
        typingPrefix: "We'll teach you to ",
        typingWord: { text: "work with AI.", color: "#F97316" },
        statement:
          "Hands-on workshops right at your company. No unnecessary theory — just methods your team will use the very next day.",
        subheadline:
          "We'll teach you and your team to effectively use AI tools like ChatGPT, Claude, and Gemini through real examples from your practice. Save hours of work daily and gain a competitive edge.",
        cta: "Get a free consultation",
      },
      offer: {
        title: "What will you learn?",
        subtitle: "Every training is customized to your industry and needs.",
        cards: [
          {
            title: "ChatGPT, Claude, Gemini in practice",
            description:
              "How to write prompts that work. Using AI for creating texts, emails, proposals, or customer communication — with practical examples from your business.",
          },
          {
            title: "AI for content creation",
            description:
              "Generating images, videos, and marketing content with AI tools. Social media posts, ads, and presentations in a fraction of the time.",
          },
          {
            title: "AI search and research",
            description:
              "How to use AI for quick market research, competitor analysis, or preparing materials. Save hours of manual work.",
          },
          {
            title: "Automating daily tasks",
            description:
              "Practical tools for automating repetitive tasks — from sorting emails to managing data, creating reports, and scheduling meetings.",
          },
          {
            title: "AI for data analysis",
            description:
              "How to use AI for effective data analysis, creating overviews, and making decisions based on numbers — no programming needed.",
          },
          {
            title: "Safe and ethical AI use",
            description:
              "What you can and cannot entrust to AI. The European AI Act, company data protection, GDPR, and practical rules for safe AI use in business.",
          },
        ],
      },
      audience: {
        title: "Who do we train?",
        cards: [
          {
            title: "Companies and their teams",
            description:
              "A workshop for the whole team or a specific department. We customize content precisely for your industry and daily processes.",
          },
          {
            title: "Managers and business owners",
            description: "How to use AI strategically — for better decision-making, more effective planning, and gaining a competitive edge.",
          },
          {
            title: "Individuals",
            description:
              "Want to learn to work with AI effectively on your own? Individual training tailored to your goals and pace.",
          },
          {
            title: "Freelancers",
            description:
              "AI as your personal assistant. Save hours weekly on administration, communication, and content creation.",
          },
        ],
      },
      process: {
        title: "Training tailored to you",
        subtitle:
          "No generic presentations about what AI can do. We find out what you do, come to you, and teach you exactly what you'll use right away.",
        cta: "Book a consultation",
        steps: [
          {
            title: "We identify your needs",
            description:
              "We call or meet. We find out what your team does, where they lose time, and what AI could simplify.",
          },
          {
            title: "We prepare custom content",
            description:
              "We build the training program specifically for your industry and team — with real examples from your practice, not from a textbook.",
          },
          {
            title: "We come to you",
            description:
              "We deliver the workshop at your office or online via video call. Practical, interactive, without unnecessary theory.",
          },
          {
            title: "We hand over materials",
            description:
              "After the training, you receive clear documentation and guides that the team can refer back to anytime.",
          },
          {
            title: "We're here afterwards too",
            description:
              "Have questions after the training? Reach out. We're happy to help when you encounter a specific problem in practice.",
          },
        ],
      },
      faq: {
        title: "Frequently asked questions",
        items: [
          {
            question: "Does our team need to be tech-savvy?",
            answer:
              "Basic computer and internet skills are enough. We explain everything from the ground up, clearly and without unnecessary technical jargon.",
          },
          {
            question: "How many people can attend at once?",
            answer:
              "Ideally 5–20 people, to allow room for hands-on exercises and questions. For larger groups, we split the training or adjust the format.",
          },
          {
            question: "How long does the training last?",
            answer:
              "A basic workshop is half a day (3–4 hours). For more comprehensive training, we adjust the scope — even full-day or multi-day programs.",
          },
          {
            question: "Do you come to us, or is it online?",
            answer:
              "Both. We prefer in-person meetings at your location, but we also deliver full-quality online training via video call.",
          },
          {
            question: "How much does the training cost?",
            answer:
              "It depends on scope and number of participants. You'll get a specific quote after the first consultation — free and non-binding.",
          },
          {
            question: "Do we get any materials?",
            answer:
              "Yes, after every training we provide clear documentation and guides that the team can refer back to anytime.",
          },
          {
            question: "Can the training be customized to our industry?",
            answer:
              "Yes, and that's the foundation of our work. Every training is built on real examples from your business — not generic demos.",
          },
        ],
      },
      contact: {
        subtitle: "Let's talk about it. A free consultation is no-obligation. We can call, write — or meet in person.",
      },
    },
  },
};
